/**
 * Core API - Push Notifications
 *
 * Database operations for web push notification subscriptions.
 * Uses core schema for multi-tenant isolation.
 */

import { supabase } from '@/integrations/supabase/client';
import { fetchFactoryById } from '@/core/api/factories';

// =============================================================================
// Types
// =============================================================================

export interface PushSubscription {
  id: string;
  tenant_id: string;
  factory_id: string;
  user_id: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePushSubscriptionInput {
  tenant_id: string;
  factory_id: string;
  user_id: string;
  endpoint: string;
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches a push subscription by user ID.
 */
export async function fetchPushSubscriptionByUser(
  userId: string
): Promise<PushSubscription | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('push_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Fetches a push subscription by endpoint (for checking duplicates).
 */
export async function fetchPushSubscriptionByEndpoint(
  endpoint: string
): Promise<PushSubscription | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('push_subscriptions')
    .select('*')
    .eq('endpoint', endpoint)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Fetches all push subscriptions for a factory.
 */
export async function fetchPushSubscriptionsByFactory(
  factoryId: string
): Promise<PushSubscription[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('push_subscriptions')
    .select('*')
    .eq('factory_id', factoryId);

  if (error) throw error;
  return data;
}

/**
 * Fetches all push subscriptions for a tenant.
 */
export async function fetchPushSubscriptionsByTenant(
  tenantId: string
): Promise<PushSubscription[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('push_subscriptions')
    .select('*')
    .eq('tenant_id', tenantId);

  if (error) throw error;
  return data;
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Creates or updates a push subscription for a user.
 * Uses upsert to handle the case where a user already has a subscription.
 */
export async function upsertPushSubscription(
  input: CreatePushSubscriptionInput
): Promise<PushSubscription> {
  const { data, error } = await supabase
    .schema('core')
    .from('push_subscriptions')
    .upsert(
      {
        tenant_id: input.tenant_id,
        factory_id: input.factory_id,
        user_id: input.user_id,
        endpoint: input.endpoint,
        p256dh: input.p256dh,
        auth: input.auth,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: 'user_id',
      }
    )
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Deletes a push subscription by ID.
 */
export async function deletePushSubscription(subscriptionId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('push_subscriptions')
    .delete()
    .eq('id', subscriptionId);

  if (error) throw error;
}

/**
 * Deletes a push subscription by user ID.
 */
export async function deletePushSubscriptionByUser(userId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('push_subscriptions')
    .delete()
    .eq('user_id', userId);

  if (error) throw error;
}

/**
 * Deletes a push subscription by endpoint (when unsubscribing).
 */
export async function deletePushSubscriptionByEndpoint(endpoint: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('push_subscriptions')
    .delete()
    .eq('endpoint', endpoint);

  if (error) throw error;
}

/**
 * Saves a push subscription to the database for a user/factory.
 * Automatically looks up the tenant_id from the factory.
 */
export async function savePushSubscriptionForFactory(
  userId: string,
  factoryId: string,
  endpoint: string,
  keys: PushSubscriptionKeys
): Promise<boolean> {
  try {
    // Look up tenant_id from factory using API wrapper
    const factory = await fetchFactoryById(factoryId);

    if (!factory) {
      console.error('Factory not found:', factoryId);
      return false;
    }

    const { error } = await supabase
      .schema('core')
      .from('push_subscriptions')
      .upsert(
        {
          user_id: userId,
          factory_id: factoryId,
          tenant_id: factory.tenant_id,
          endpoint,
          p256dh: keys.p256dh,
          auth: keys.auth,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'user_id',
        }
      );

    if (error) {
      console.error('Error saving subscription:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error saving subscription to database:', error);
    return false;
  }
}

/**
 * Removes a push subscription from the database by user and endpoint.
 */
export async function removePushSubscriptionForUser(
  userId: string,
  endpoint: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .schema('core')
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId)
      .eq('endpoint', endpoint);

    if (error) {
      console.error('Error removing subscription:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error removing subscription from database:', error);
    return false;
  }
}
